import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { getImageUrl } from '../utils/imageUtils';
import Footer from '../components/Footer';
import SimpleLoader from '../components/SimpleLoader';

const ListingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadListingDetails();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadListingDetails = async () => {
    try {
      const response = await api.get(`/listings/${id}`);
      setListing(response.data);

      // Track view (don't block the UI if this fails)
      try {
        const viewResponse = await api.post('/listings/track-view', { listingId: id });
      } catch (viewError) {
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Listing not found');
      } else if (error.response?.status === 401) {
        toast.error('Please log in to view listing details');
        navigate('/login');
        return;
      } else {
        toast.error('Failed to load listing details');
      }
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (listing) => {
    if (new Date(listing.expiresAt) <= new Date()) {
      return <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">Expired</span>;
    } else if (listing.isActive) {
      return <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">Active</span>;
    } else {
      return <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: '#948979', color: 'white', opacity: 0.8 }}>Inactive</span>;
    }
  };

  const getExpiryInfo = (listing) => {
    const expiry = new Date(listing.expiresAt);
    const now = new Date();
    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

    if (daysLeft <= 0) {
      return { text: 'Expired', color: 'text-red-600' };
    } else if (daysLeft <= 3) {
      return { text: `${daysLeft} days left`, color: 'text-orange-600' };
    } else {
      return { text: `${daysLeft} days left`, color: 'text-green-600' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F6F1EB' }}>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center py-16">
            <SimpleLoader size="xl" text="Loading listing details..." />
          </div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F6F1EB' }}>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center py-16">
            <i className="fas fa-exclamation-triangle text-4xl mb-4" style={{ color: '#948979' }}></i>
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#222831' }}>Listing not found</h2>
            <p className="mb-6" style={{ color: '#393E46' }}>The listing you're looking for doesn't exist or has been removed.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const expiryInfo = getExpiryInfo(listing);

  return (
    <>
      <Helmet>
        <title>Item Details - VestrioZ</title>
        <meta
          name="description"
          content="View detailed information about this fashion or jewellery item available for rent."
        />
        <meta name="keywords" content="item details, listing details, VestrioZ" />
      </Helmet>
      <div className="min-h-screen" style={{ backgroundColor: '#F6F1EB' }}>
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Header with Back Button */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center text-gray-600 hover:text-gray-800 transition-colors mb-4"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Dashboard
            </button>
            <h1 className="text-3xl md:text-4xl font-bold" style={{ color: '#222831' }}>
              Listing Details
            </h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Image Section */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              {listing.images && listing.images.length > 0 ? (
                <div className="w-full h-96 flex items-center justify-center bg-gray-50">
                  <div className="w-full h-full flex items-center justify-center p-4">
                    <img
                      src={getImageUrl(listing.images[0])}
                      alt={listing.itemName}
                      className="max-w-full max-h-full object-contain rounded-lg"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="w-full h-96 flex items-center justify-center bg-gray-100 text-gray-500">
                  <div className="text-center">
                    <i className="fas fa-image text-4xl mb-2"></i>
                    <p>No images available</p>
                  </div>
                </div>
              )}
            </div>

            {/* Details Section */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              {/* Title and Price */}
              <div className="mb-6">
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#222831' }}>
                    {listing.itemName}
                  </h2>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-indigo-600">₹{listing.price}</div>
                    <div className="text-sm text-gray-600">per day</div>
                  </div>
                </div>

                {/* Status and Expiry */}
                <div className="flex flex-wrap gap-3 mb-4">
                  {getStatusBadge(listing)}
                  <span className={`text-sm font-medium ${expiryInfo.color}`}>
                    <i className="fas fa-calendar mr-1"></i>
                    {expiryInfo.text}
                  </span>
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                    <i className="fas fa-eye mr-1"></i>
                    {listing.views || 0} views
                  </span>
                  {listing.isRented && (
                    <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                      <i className="fas fa-handshake mr-1"></i>
                      Currently Rented
                    </span>
                  )}
                </div>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Category</label>
                  <p className="text-lg font-semibold capitalize" style={{ color: '#222831' }}>
                    {listing.category || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Size</label>
                  <p className="text-lg font-semibold" style={{ color: '#222831' }}>
                    {listing.itemType === 'jewellery' ? (listing.jewellerySize || 'Free Size') : listing.size}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Type</label>
                  <p className="text-lg font-semibold capitalize" style={{ color: '#222831' }}>
                    {listing.itemType === 'jewellery' ? listing.jewelleryType : listing.clothingType}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Condition</label>
                  <p className="text-lg font-semibold capitalize" style={{ color: '#222831' }}>
                    {listing.condition}
                  </p>
                </div>
              </div>

              {/* Location */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-600 mb-1">Location</label>
                <p className="text-lg font-semibold flex items-center" style={{ color: '#222831' }}>
                  <i className="fas fa-map-marker-alt text-red-500 mr-2"></i>
                  {listing.location}
                </p>
              </div>

              {/* Description */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-600 mb-2">Description</label>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-800 leading-relaxed">{listing.description}</p>
                </div>
              </div>

              {/* Tags */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-600 mb-2">Tags</label>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium capitalize">
                    <i className={`fas ${listing.itemType === 'jewellery' ? 'fa-gem' : 'fa-tag'} mr-1`}></i>
                    {listing.itemType === 'jewellery' ? listing.jewelleryType : listing.clothingType}
                  </span>
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium capitalize">
                    <i className="fas fa-star mr-1"></i>
                    {listing.condition}
                  </span>
                  <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium capitalize">
                    <i className="fas fa-venus-mars mr-1"></i>
                    {listing.category || 'Handycraft'}
                  </span>
                </div>
              </div>

              {/* Listing Info */}
              <div className="border-t pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Listed on:</span>
                    <span className="ml-2">{new Date(listing.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div>
                    <span className="font-medium">Last updated:</span>
                    <span className="ml-2">{new Date(listing.updatedAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Rental Details Section (if rented) */}
          {listing.isRented && listing.rentalDetails && (
            <div className="mt-8 bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-2xl font-bold mb-6" style={{ color: '#222831' }}>
                <i className="fas fa-handshake mr-2 text-blue-600"></i>
                Rental Information
              </h3>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-blue-600 mb-1">Renter Name</label>
                      <p className="text-lg font-semibold text-blue-800">{listing.rentalDetails.renterName}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-blue-600 mb-1">Contact Number</label>
                      <p className="text-lg font-semibold text-blue-800">{listing.rentalDetails.renterContact}</p>
                    </div>

                    {listing.rentalDetails.renterLocation && (
                      <div>
                        <label className="block text-sm font-medium text-blue-600 mb-1">Renter Location</label>
                        <p className="text-lg font-semibold text-blue-800">{listing.rentalDetails.renterLocation}</p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-blue-600 mb-1">Rented On</label>
                      <p className="text-lg font-semibold text-blue-800">
                        {new Date(listing.rentalDetails.rentedAt).toLocaleDateString()}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-blue-600 mb-1">Expected Return Date</label>
                      <p className="text-lg font-semibold text-blue-800">
                        {new Date(listing.rentalDetails.expectedReturnDate).toLocaleDateString()}
                      </p>
                      {(() => {
                        const returnDate = new Date(listing.rentalDetails.expectedReturnDate);
                        const today = new Date();
                        const daysUntilReturn = Math.ceil((returnDate - today) / (1000 * 60 * 60 * 24));
                        const isOverdue = daysUntilReturn < 0;

                        if (isOverdue) {
                          return (
                            <p className="text-red-600 font-bold text-sm mt-1">
                              <i className="fas fa-exclamation-triangle mr-1"></i>
                              {Math.abs(daysUntilReturn)} days overdue!
                            </p>
                          );
                        } else if (daysUntilReturn === 0) {
                          return (
                            <p className="text-orange-600 font-bold text-sm mt-1">
                              <i className="fas fa-clock mr-1"></i>
                              Due today
                            </p>
                          );
                        } else {
                          return (
                            <p className="text-green-600 font-medium text-sm mt-1">
                              <i className="fas fa-check-circle mr-1"></i>
                              {daysUntilReturn} days remaining
                            </p>
                          );
                        }
                      })()}
                    </div>
                  </div>
                </div>

                {listing.rentalDetails.notes && (
                  <div className="mt-6 pt-4 border-t border-blue-200">
                    <label className="block text-sm font-medium text-blue-600 mb-2">Rental Notes</label>
                    <p className="text-blue-800 italic bg-white/50 p-3 rounded-lg">{listing.rentalDetails.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
};

export default ListingDetails;